import { Skeleton, Space, Spin, Table, Tag } from "antd"
import InvoiceDateDisplay from "./InvoiceDateDisplay"
import InvoiceNumberDisplay from "./InvoiceNumberDisplay"
import { centsToDollarsDisplay } from "../../displayFormatters"
import { useAllInvoiceSummariesQuery } from "../../../domain/invoicing/invoicingQueries"
import BalanceDisplay from "./BalanceDisplay"
import { getPaymentMethodDisplay, InvoiceSummaryWithCustomerJobData, isInvoiceBulkSelectable } from "../../../domain/invoicing/invoice"
import { EmailStatusDisplay } from "./EmailStatusDisplay"
import QuickBooksTrackingState from "./QuickBooksTrackingState"
import { useContext, useEffect, useState } from "react"
import { AllInvoicesRequest } from "../../../domain/invoicing/invoicingApi"
import InvoiceActions from "./InvoiceActions"
import StatementActions from "./StatementActions"
import StatementJobBadge from "../StatementJobBadge"
import { AllInvoicesFilterValues } from "../AllInvoices/AllInvoicesFilters"
import InvoiceActionsContext from "../AllInvoices/InvoiceActionsContext"
import { toEmailFilter } from "../emailFilter"
import { shouldShowTrackingColumn } from "../../../domain/invoicing/quickBooksTrackingModel"
import { AuthenticationContext } from "../../../AuthenticationContext"
import { StatementSummary } from "../../../domain/invoicing/statement"
import { useStatementMembersQueries } from "../../../domain/invoicing/statementsQueries"
import { applyLoadedStatementMembers, composeInvoiceListRows, expandedStatementIdsFromRows, InvoiceListRow, isRowClickable, loadedMembersByStatementId } from "./invoiceListRowsModel"

export type InvoiceListMetadata = {
  totalUnpaid: number
  totalOverdue: number
  totalPaid: number
  totalCount: number
  totalInvoicedCents: number
  sentCount: number
}

type Props = {
  filters?: AllInvoicesFilterValues
  onMetadataChange?: (metadata: InvoiceListMetadata) => void
  onSummariesLoaded?: (summaries: InvoiceSummaryWithCustomerJobData[]) => void
  onStatementsLoaded?: (statements: StatementSummary[]) => void
  isRowSelected?: (invoiceId: string) => boolean
  onToggleRow?: (summary: InvoiceSummaryWithCustomerJobData) => void
  isStatementSelected?: (statementId: string) => boolean
  onToggleStatement?: (statement: StatementSummary) => void
}

const mutedDash = <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>—</span>

const statementStatusTag = (statement: StatementSummary) => {
  if (statement.status === 'paid') {
    return <Tag color="processing">Paid</Tag>
  }
  if (statement.status === 'sent') {
    return <Tag color="success">Sent</Tag>
  }
  return <Tag>Open</Tag>
}

const statementQboStatus = (statement: StatementSummary) => {
  if (statement.qboSyncState === 'synced') {
    return <Tag color="success">Synced</Tag>
  }
  if (statement.qboSyncState === 'partial') {
    return <Tag color="warning">Partial</Tag>
  }
  return <Tag color="error">Not Synced</Tag>
}

const AllInvoiceListTable = ({ filters, onMetadataChange, onSummariesLoaded, onStatementsLoaded, isRowSelected, onToggleRow, isStatementSelected, onToggleStatement }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const { openModal } = useContext(InvoiceActionsContext)
  const [request, setRequest] = useState<AllInvoicesRequest>({
    page: 1,
    pageSize: 50,
    sortBy: 'CreatedAt',
    sortDescending: true,
    emailFilter: {
      sent: true,
      notSent: true
    }
  })

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])

  useEffect(() => {
    setRequest(prev => ({ ...prev, page: 1 }))
  }, [filters])

  const fullRequest: AllInvoicesRequest = {
    ...request,
    paidStatus: filters?.paidStatus,
    search: filters?.search || undefined,
    customerId: filters?.customerId,
    jobType: filters?.jobType,
    divisionId: filters?.divisionId,
    qboStatus: filters?.qboStatus,
    creationDateStart: filters?.creationDateStart,
    creationDateEnd: filters?.creationDateEnd,
    dueDateStart: filters?.dueDateStart,
    dueDateEnd: filters?.dueDateEnd,
    emailFilter: toEmailFilter(filters?.emailStatus),
    page: request.page,
    pageSize: request.pageSize,
  }

  const summariesQuery = useAllInvoiceSummariesQuery(fullRequest)

  useEffect(() => {
    if (summariesQuery.data) {
      onMetadataChange?.({
        totalUnpaid: summariesQuery.data.totalUnpaid,
        totalOverdue: summariesQuery.data.totalOverdue,
        totalPaid: summariesQuery.data.totalPaid,
        totalCount: summariesQuery.data.summaryCount,
        totalInvoicedCents: summariesQuery.data.totalInvoicedCents,
        sentCount: summariesQuery.data.sentCount,
      })
      onSummariesLoaded?.(summariesQuery.data.summaries)
      onStatementsLoaded?.(summariesQuery.data.statements ?? [])
    }
  }, [summariesQuery.data, onMetadataChange, onSummariesLoaded, onStatementsLoaded])

  const summaries = summariesQuery.data?.summaries ?? []
  const statements = summariesQuery.data?.statements ?? []
  const baseRows = composeInvoiceListRows(summaries, statements, summariesQuery.data?.rows)

  const expandedStatementIds = expandedStatementIdsFromRows(baseRows, expandedRowKeys)
  const memberResults = useStatementMembersQueries(expandedStatementIds)
  const membersByStatementId = loadedMembersByStatementId(expandedStatementIds, memberResults)

  if (summariesQuery.isLoading) {
    return <Skeleton active />
  }

  const isTenantConnectedToQuickBooks = currentUser?.isTenantConnectedToQuickBooks === true
  const showQuickBooksColumn = shouldShowTrackingColumn(isTenantConnectedToQuickBooks, summaries)
  const rows = applyLoadedStatementMembers(baseRows, membersByStatementId)

  const toggleSelectableRow = (row: InvoiceListRow) => {
    if (row.rowType === 'invoice') {
      onToggleRow?.(row.invoice)
    } else if (row.rowType === 'statement') {
      onToggleStatement?.(row.statement)
    }
  }

  const rowSelection = isRowSelected && onToggleRow ? {
    selectedRowKeys: rows.filter(row => {
      if (row.rowType === 'invoice') {
        return isRowSelected(row.invoice.invoiceId)
      }
      if (row.rowType === 'statement') {
        return isStatementSelected?.(row.statement.statementId) ?? false
      }
      return false
    }).map(row => row.key),
    onSelect: (record: InvoiceListRow) => toggleSelectableRow(record),
    onSelectAll: (_selected: boolean, _selectedRows: InvoiceListRow[], changeRows: InvoiceListRow[]) => {
      changeRows.forEach(toggleSelectableRow)
    },
    getCheckboxProps: (record: InvoiceListRow) => ({
      disabled: record.rowType === 'childInvoice'
        || record.rowType === 'loadingMembers'
        || (record.rowType === 'invoice' && !isInvoiceBulkSelectable(record.invoice))
        || (record.rowType === 'statement' && !onToggleStatement)
    })
  } : undefined

  return <Table<InvoiceListRow>
    size="small"
    dataSource={rows}
    rowKey={row => row.key}
    rowSelection={rowSelection}
    expandable={{
      expandedRowKeys,
      onExpandedRowsChange: keys => setExpandedRowKeys(keys as string[]),
      rowExpandable: row => row.rowType === 'statement' && row.statement.invoiceCount > 0
    }}
    scroll={{ x: 'max-content' }}
    columns={[
      {
        title: 'Customer',
        render: (_, row) => {
          if (row.rowType === 'statement') {
            return row.statement.customerDisplayName
          }
          if (row.rowType === 'childInvoice' || row.rowType === 'loadingMembers') {
            return ''
          }
          return row.invoice.customerDisplayName
        }
      },
      {
        title: 'Job',
        render: (_, row) => {
          if (row.rowType === 'statement') {
            return <StatementJobBadge count={row.statement.invoiceCount} />
          }
          if (row.rowType === 'loadingMembers') {
            return <Space size="small"><Spin size="small" />Loading...</Space>
          }
          return row.invoice.jobName
        }
      },
      {
        title: 'Invoice Number',
        render: (_, row) => {
          if (row.rowType === 'statement') {
            return row.statement.statementNumber
          }
          if (row.rowType === 'loadingMembers') {
            return ''
          }
          return <InvoiceNumberDisplay invoiceSummary={row.invoice} />
        }
      },
      {
        title: 'Invoice Total',
        align: 'right' as const,
        render: (_, row) => {
          if (row.rowType === 'loadingMembers') {
            return ''
          }
          return centsToDollarsDisplay(row.rowType === 'statement' ? row.statement.total : row.invoice.total)
        }
      },
      {
        title: 'Balance',
        align: 'right' as const,
        render: (_, row) => {
          if (row.rowType === 'childInvoice' || row.rowType === 'loadingMembers') {
            return mutedDash
          }
          const source = row.rowType === 'statement' ? row.statement : row.invoice
          return <BalanceDisplay
            balance={source.balance}
            isFullyPaid={source.balance === 0}
            hasPaymentProblem={source.hasPaymentProblem}
            hasPendingPayment={source.hasPendingPayment}
          />
        }
      },
      {
        title: 'Sent Status',
        render: (_, row) => {
          if (row.rowType === 'statement') {
            return statementStatusTag(row.statement)
          }
          if (row.rowType === 'childInvoice' || row.rowType === 'loadingMembers') {
            return mutedDash
          }
          return <EmailStatusDisplay invoiceSummary={row.invoice} />
        }
      },
      ...(showQuickBooksColumn ? [{
        title: 'QuickBooks',
        render: (_: any, row: InvoiceListRow) => {
          if (row.rowType === 'statement') {
            return statementQboStatus(row.statement)
          }
          if (row.rowType === 'childInvoice' || row.rowType === 'loadingMembers') {
            return mutedDash
          }
          return <QuickBooksTrackingState invoiceSummary={row.invoice} />
        }
      }] : []),
      {
        title: 'Due',
        render: (_, row) => {
          if (row.rowType === 'childInvoice' || row.rowType === 'loadingMembers') {
            return ''
          }
          const dueOn = row.rowType === 'statement' ? row.statement.dueOn : row.invoice.dueOn
          return dueOn ? <InvoiceDateDisplay date={dueOn} /> : ''
        }
      },
      {
        title: 'Payment Method',
        render: (_, row) => row.rowType === 'invoice' ? getPaymentMethodDisplay(row.invoice) : ''
      },
      {
        title: '',
        width: 50,
        render: (_, row) => {
          if (row.rowType === 'statement') {
            return <StatementActions statement={row.statement} />
          }
          if (row.rowType === 'invoice') {
            return <InvoiceActions invoiceSummary={row.invoice} />
          }
          if (row.rowType === 'childInvoice' && !row.statement.wasEmailSent) {
            return <InvoiceActions invoiceSummary={row.invoice} parentStatement={row.statement} />
          }
          return null
        }
      },
    ]}
    onRow={(record) => ({
      onClick: (e) => {
        const target = e.target as HTMLElement
        if (target.closest('input[type="checkbox"]')) {
          return
        }
        if (target.closest('.ant-dropdown')) {
          return
        }
        if (isRowClickable(record) && (record.rowType === 'invoice' || record.rowType === 'childInvoice')) {
          openModal('viewInvoice', record.invoice)
        }
      },
      style: { cursor: isRowClickable(record) ? 'pointer' : 'default' }
    })}
    pagination={{
      current: request.page,
      pageSize: request.pageSize,
      total: summariesQuery.data?.summaryCount,
      hideOnSinglePage: true,
      onChange: (page, pageSize) => {
        setRequest({...request, page, pageSize})
      }
    }}
    locale={{emptyText: 'No invoices found.'}}
  />
}

export default AllInvoiceListTable
